import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileArchive, Info, FolderTree, FileText, CheckCircle2, ChevronDown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { UploadDropzone } from './upload-dropzone';
import autoUploadsAPI from '@/api/auto-uploads.api';

export function UploadInterface({ workspaces = [] }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractMetadata, setExtractMetadata] = useState(true);

  const fileInputRef = useRef(null);

  const handleFileSelect = file => {
    if (file === null) {
      fileInputRef.current?.click();
      return;
    }
    setSelectedFile(file);
  };

  const handleFileInputChange = event => {
    const file = event.target.files?.[0];
    if (file) {
      const isValidType =
        file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
      const isValidSize = file.size <= 1024 * 1024 * 1024; // 1GB

      if (!isValidType) {
        toast({
          title: 'Invalid file type',
          description: 'Only ZIP files are allowed.',
          variant: 'destructive',
        });
        return;
      }

      if (!isValidSize) {
        toast({
          title: 'File too large',
          description: 'File size must be less than 1GB.',
          variant: 'destructive',
        });
        return;
      }

      setSelectedFile(file);
    }
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedWorkspace) {
      toast({
        title: 'Missing Information',
        description: 'Please select a file and workspace before uploading.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const uploadParams = {
        workspaceId: selectedWorkspace,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        contentType: 'application/zip',
      };

      if (selectedCategory && selectedCategory !== 'none') {
        uploadParams.parentCategoryId = selectedCategory;
      }

      const sessionResult = await autoUploadsAPI.createUpload(uploadParams);

      if (sessionResult.error) {
        throw new Error(sessionResult.error.message || 'Failed to create upload session');
      }

      const { uploadId, signedUrl } = sessionResult.data;

      await autoUploadsAPI.uploadToGCS(signedUrl, selectedFile, progressEvent => {
        if (progressEvent.lengthComputable) {
          const percentComplete = (progressEvent.loaded / progressEvent.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Show toast after GCS upload completes
      const uploadToast = toast({
        title: 'Upload Started',
        description: `${selectedFile.name} is being uploaded. You can navigate away and check progress.`,
        duration: Infinity,
        action: (
          <button
            onClick={() => {
              uploadToast.dismiss();
              navigate(`?tab=history&uploadId=${uploadId}`);
            }}
            className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
            View Progress
          </button>
        ),
      });

      const mappingResult = await autoUploadsAPI.applyMapping(uploadId, {
        parentCategoryId: selectedCategory === 'none' ? undefined : selectedCategory,
      });

      if (mappingResult.error) {
        throw new Error(mappingResult.error.message || 'Failed to map upload structure');
      }

      const startResult = await autoUploadsAPI.startUploadProcessing(uploadId, {
        skipMetadataExtraction: !extractMetadata,
      });

      if (startResult.error) {
        console.error('Error starting upload processing:', startResult.error);
        toast({
          title: 'Processing Not Started',
          description:
            'The ZIP was uploaded and mapped, but processing failed to start. Open the upload in the history tab and use "Start Processing" to retry.',
          variant: 'destructive',
        });
      }

      // Reset form
      setSelectedFile(null);
      setSelectedWorkspace('');
      setSelectedCategory('');
      setUploadProgress(0);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'An error occurred during upload.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setIsUploading(false);
    setSelectedWorkspace('');
    setSelectedCategory('');
    setUploadProgress(0);
    setExtractMetadata(true);
  };

  const formatFileSize = bytes => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <Card>
        <CardHeader>
          <CardTitle>Bulk Document Upload</CardTitle>
          <CardDescription>
            Upload ZIP files containing documents for this organization. Files will be automatically
            extracted and processed into the knowledge system.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* ZIP Structure Instructions */}
          <Instructions />

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="workspace">Target Workspace *</Label>
            <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
              <SelectTrigger className={!selectedWorkspace ? 'border-red-500 border-2' : ''}>
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map(workspace => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* COMING SOON
              <div className="space-y-2">
                <Label htmlFor="category">Parent Category (Optional)</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a parent category (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No parent category (use root)</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

          <div className="flex items-center space-x-3">
            <Checkbox
              id="extract-metadata"
              checked={extractMetadata}
              onCheckedChange={setExtractMetadata}
            />
            <div>
              <Label htmlFor="extract-metadata" className="cursor-pointer font-medium">
                Extract metadata after upload
              </Label>
              <p className="text-xs text-muted-foreground">
                Automatically extract title, keywords, specialty, and other metadata from uploaded
                documents using AI. You can also trigger this manually later if you want control
                over the extracted fields.
              </p>
            </div>
          </div>

          <Separator />

          {!selectedFile ? (
            <UploadDropzone onFileSelect={handleFileSelect} disabled={isUploading} />
          ) : (
            <Card
              className="border-2 border-dashed border-primary/50 bg-primary/5 cursor-pointer hover:border-primary transition-colors"
              onClick={() => !isUploading && handleFileSelect(null)}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileArchive className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">{selectedFile.name}</h4>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)} • Click to change file
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    if (!isUploading) handleFileSelect(null);
                  }}
                  disabled={isUploading}>
                  Change
                </Button>
              </CardContent>
            </Card>
          )}

          {isUploading && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Uploading to cloud storage...</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !selectedWorkspace || isUploading}
              className="flex-1">
              {isUploading ? 'Uploading...' : 'Start Upload'}
            </Button>
            {selectedFile && (
              <Button variant="outline" onClick={handleReset} disabled={isUploading}>
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const Instructions = () => {
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  return (
    <Collapsible open={isInstructionsOpen} onOpenChange={setIsInstructionsOpen}>
      <Alert>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <AlertTitle className="mb-0">ZIP File Structure Guidelines</AlertTitle>
            </div>
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${
                isInstructionsOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AlertDescription className="mt-3 space-y-3">
            <div className="text-sm">
              <p className="font-medium mb-2">
                Organize your ZIP file with a hierarchical folder structure. The system uses an even
                number of folder levels:
              </p>
              <div className="space-y-2 pl-4 border-l-2 border-muted">
                <div className="flex items-start gap-2">
                  <FolderTree className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Category folders</span> (e.g.,
                    &quot;Cardiology&quot;, &quot;Neurology&quot;) organize and group content
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FolderTree className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Section folders</span> (e.g., &quot;Clinical
                    Guidelines&quot;, &quot;Patient Education&quot;) organize binders within
                    categories
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FolderTree className="h-4 w-4 mt-0.5 text-green-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Binder folders</span> (e.g., &quot;Heart Failure
                    Protocols&quot;, &quot;Stroke Management&quot;) contain the actual documents
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-0.5 text-purple-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Documents</span> (PDF, DOC, etc.) are placed
                    inside binder folders
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-md font-mono text-xs">
              <div className="font-semibold mb-1 text-foreground">
                Example structure (Category → Section → Binder):
              </div>
              <div className="space-y-0.5 text-muted-foreground">
                <div>📦 medical-content.zip</div>
                <div className="pl-3">📁 Cardiology/ (Category)</div>
                <div className="pl-6">📁 Clinical Guidelines/ (Section)</div>
                <div className="pl-9">📁 Heart Failure/ (Binder)</div>
                <div className="pl-12">📄 heart-failure-protocol.pdf</div>
                <div className="pl-12">📄 acute-coronary-syndrome.pdf</div>
                <div className="pl-6">📁 Patient Education/ (Section)</div>
                <div className="pl-9">📁 Hypertension/ (Binder)</div>
                <div className="pl-12">📄 managing-hypertension.pdf</div>
                <div className="pl-3">📁 Neurology/ (Category)</div>
                <div className="pl-6">📁 Treatment Protocols/ (Section)</div>
                <div className="pl-9">📁 Stroke/ (Binder)</div>
                <div className="pl-12">📄 stroke-management.pdf</div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                <span>
                  <strong>Supported formats:</strong> PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT,
                  CSV, images, and more
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                <span>
                  <strong>Max file size:</strong> 100MB per document, 1GB total ZIP size
                </span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                <span>
                  <strong>Auto-skip:</strong> System files (.DS_Store, Thumbs.db, __MACOSX) are
                  automatically ignored
                </span>
              </div>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p className="italic">
                💡 <strong>Tip:</strong> The system requires an even number of folder levels before
                the binder (e.g., Category → Section → Binder). If you have an odd number, a default
                &quot;Files&quot; binder will be added automatically.
              </p>
              <p className="italic">
                📌 <strong>Create sections in Knowledge Base root:</strong> use just Section →
                Binder (2 levels).
              </p>
            </div>
          </AlertDescription>
        </CollapsibleContent>
      </Alert>
    </Collapsible>
  );
};
